import { isMobileQuery, configQuery } from '@gig/magic-common';
import { LANGUAGE_UPDATED, GAMES_LOADED, USER_JURISDICTION_UPDATED, GAMES_RECOMMENDED_GAMES, GAMES_LOBBIES_UPDATED, GAMES_CATEGORIES_LIST, GAMES_PROVIDERS, USER_JURISDICTION_QUERY, BALANCE_CURRENCY_QUERY, GAME_COREX_FETCH_BY_ID } from '@gig/magic-messages';
import { __awaiter, __generator, __assign } from 'tslib';
import { magic } from '@gig/magic-sdk';
import { Environment } from '../config/toolkit-config.interface.js';
import '../config/config-v2.service.js';
import { Jurisdiction } from '@gig/magic-jurisdictions/dist/jurisdictions.constants';
import { openGamesDB } from './game-db.js';
import { openGamesCorexDB } from './game-db-corex.js';

var GamesFetchCoreXService = /** @class */ (function () {
    function GamesFetchCoreXService(gamesService) {
        var _this = this;
        this.gamesService = gamesService;
        this.language = '';
        this.gamesResponse = null;
        this.gamesLoaded = false;
        this.defaultLobby = '';
        this.defaultCategories = [];
        this.ignoreCurrencyGamesFiltering = false;
        this.ignoreJurisdictionGamesFiltering = false;
        Promise.all([
            configQuery('defaultLobby'),
            configQuery('defaultGameCategories'),
            configQuery('ignoreCurrencyGamesFiltering'),
            configQuery('ignoreJurisdictionGamesFiltering'),
        ]).then(function (_a) {
            var defaultLobby = _a[0], defaultCategories = _a[1], ignoreCurrencyGamesFiltering = _a[2], ignoreJurisdictionGamesFiltering = _a[3];
            _this.defaultLobby = defaultLobby;
            _this.defaultCategories = defaultCategories;
            _this.ignoreCurrencyGamesFiltering =
                ignoreCurrencyGamesFiltering !== null && ignoreCurrencyGamesFiltering !== void 0 ? ignoreCurrencyGamesFiltering : false;
            _this.ignoreJurisdictionGamesFiltering =
                ignoreJurisdictionGamesFiltering !== null && ignoreJurisdictionGamesFiltering !== void 0 ? ignoreJurisdictionGamesFiltering : false;
            _this.subscribeToLanguageChange();
            _this.subscribeToGameCorex();
        });
    }
    GamesFetchCoreXService.prototype.subscribeToLanguageChange = function () {
        var _this = this;
        window.eventBus.subscribe(LANGUAGE_UPDATED, function (event) { return __awaiter(_this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.gamesLoaded = false;
                        window.eventBus.publish(GAMES_LOADED, false);
                        this.language = event.payload;
                        _a = this;
                        return [4 /*yield*/, this.fetchGames(this.language)];
                    case 1:
                        _a.gamesResponse = _b.sent();
                        return [4 /*yield*/, this.publishCategories()];
                    case 2:
                        _b.sent();
                        return [4 /*yield*/, this.publishGames()];
                    case 3:
                        _b.sent();
                        this.gamesLoaded = true;
                        window.eventBus.publish(GAMES_LOADED, true);
                        return [2 /*return*/];
                }
            });
        }); }, { replay: true, distinct: true });
        window.eventBus.subscribe(USER_JURISDICTION_UPDATED, function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.gamesLoaded) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, this.publishGames()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); }, { distinct: true });
    };
    GamesFetchCoreXService.prototype.fetchGames = function (language) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, isMobile, environment, device, env, url, payload, json;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, Promise.all([
                            isMobileQuery(),
                            configQuery('environment'),
                        ])];
                    case 1:
                        _a = _b.sent(), isMobile = _a[0], environment = _a[1];
                        device = isMobile ? '_m' : '';
                        env = environment === Environment.PRODUCTION ? '' : '-staging';
                        url = magic.transport.getCDNUrl() + "/games/" + language + device + env + ".json";
                        return [4 /*yield*/, magic.transport.getHttpDriver().get(url)];
                    case 2:
                        payload = _b.sent();
                        json = JSON.parse(payload);
                        return [2 /*return*/, json.categories || json];
                }
            });
        });
    };
    GamesFetchCoreXService.prototype.publishCategories = function () {
        return __awaiter(this, void 0, void 0, function () {
            var categories, coreCategories, lobbies, categoriesPerLobby;
            var _this = this;
            return __generator(this, function (_a) {
                categories = this.extractAllCategories();
                coreCategories = categories
                    .map(function (category) { return _this.mapToCoreCategory(category); })
                    .sort(function (a) { return (_this.defaultCategories.includes(a.slug) ? -1 : 1); });
                lobbies = this.getUniqueLobbies(coreCategories);
                categoriesPerLobby = this.getCategoriesPerLobby(lobbies, coreCategories);
                window.eventBus.publish(GAMES_RECOMMENDED_GAMES, []);
                window.eventBus.publish(GAMES_LOBBIES_UPDATED, categoriesPerLobby);
                window.eventBus.publish(GAMES_CATEGORIES_LIST, coreCategories);
                window.eventBus.disableHistory(GAMES_CATEGORIES_LIST);
                return [2 /*return*/];
            });
        });
    };
    GamesFetchCoreXService.prototype.getUniqueLobbies = function (categories) {
        return Array.from(new Set(categories.map(function (category) { return category.lobby; })));
    };
    GamesFetchCoreXService.prototype.getCategoriesPerLobby = function (lobbies, categories) {
        return lobbies.reduce(function (acc, lobby) {
            var _a;
            return (__assign(__assign({}, acc), (_a = {}, _a[lobby] = categories
                .filter(function (category) { return category.lobby === lobby; })
                .map(function (category) { return category.slug; }), _a)));
        }, {});
    };
    GamesFetchCoreXService.prototype.publishGames = function () {
        return __awaiter(this, void 0, void 0, function () {
            var games, _a, jurisdiction, currency, categories, coreCategories, db, dbX;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        games = new Map();
                        return [4 /*yield*/, Promise.all([
                                window.queryBus.query(USER_JURISDICTION_QUERY),
                                window.queryBus.query(BALANCE_CURRENCY_QUERY),
                            ])];
                    case 1:
                        _a = _b.sent(), jurisdiction = _a[0], currency = _a[1];
                        categories = this.extractAllCategories();
                        categories.forEach(function (category) {
                            var _a;
                            var filteredGames = ((_a = category.games) !== null && _a !== void 0 ? _a : []).filter(function (game) {
                                var isSweepstakes = jurisdiction === Jurisdiction.SWEEPSTAKES;
                                var jurisdictionAllowed = _this.ignoreJurisdictionGamesFiltering ||
                                    isSweepstakes ||
                                    game.jurisdictionCodes.includes(jurisdiction);
                                var currencyAllowed = _this.ignoreCurrencyGamesFiltering ||
                                    isSweepstakes ||
                                    Boolean(game.aggregatesByCurrency[currency]);
                                return jurisdictionAllowed && currencyAllowed;
                            });
                            filteredGames.forEach(function (game) {
                                var mappedGame = games.get(game.slug);
                                if (mappedGame) {
                                    _this.amendCoreGame(category, mappedGame);
                                }
                                else {
                                    games.set(game.slug, _this.mapToCoreGame(category, game));
                                }
                            });
                        });
                        window.eventBus.publish(GAMES_PROVIDERS, this.getGameProviders(games));
                        coreCategories = categories.map(function (category) {
                            return _this.mapToCoreCategory(category);
                        });
                        return [4 /*yield*/, openGamesDB()];
                    case 2:
                        db = _b.sent();
                        Promise.all([db.put(games), db.putCategories(coreCategories)]).then(function () {
                            games.clear();
                            db.close();
                            _this.gamesResponse = [];
                        });
                        this.gamesService.updateViewGames(games);
                        return [4 /*yield*/, openGamesCorexDB()];
                    case 3:
                        dbX = _b.sent();
                        return [4 /*yield*/, dbX.put(this.buildGameMap(categories))];
                    case 4:
                        _b.sent();
                        dbX.close();
                        return [2 /*return*/];
                }
            });
        });
    };
    GamesFetchCoreXService.prototype.getGameProviders = function (games) {
        var gamesArray = Array.from(games.values());
        var vendors = gamesArray.map(function (game) { return game.vendor; });
        return Array.from(new Set(vendors)).filter(function (vendor) { return !!vendor; });
    };
    GamesFetchCoreXService.prototype.amendCoreGame = function (category, game) {
        if (category.slug && !game.categories.includes(category.slug)) {
            game.categories.push(category.slug);
        }
        if (category.lobby && !game.lobbies.includes(category.lobby)) {
            game.lobbies.push(category.lobby);
        }
    };
    GamesFetchCoreXService.prototype.extractAllCategories = function () {
        var categories = [];
        var uniqueSlugs = new Set();
        this.gamesResponse.forEach(function (category) {
            if (!uniqueSlugs.has(category.slug)) {
                uniqueSlugs.add(category.slug);
                categories.push(category);
            }
            if (category.subs) {
                category.subs.forEach(function (subCategory) {
                    if (!uniqueSlugs.has(subCategory.slug)) {
                        uniqueSlugs.add(subCategory.slug);
                        categories.push(subCategory);
                    }
                });
            }
        });
        return Array.from(categories);
    };
    GamesFetchCoreXService.prototype.buildGameMap = function (categories) {
        var _this = this;
        var gamesMap = new Map();
        categories.forEach(function (category) {
            _this.buildGameMapForCategory(gamesMap, category);
            if (category.subs) {
                category.subs.forEach(function (subCategory) {
                    _this.buildGameMapForCategory(gamesMap, subCategory);
                });
            }
        });
        return gamesMap;
    };
    GamesFetchCoreXService.prototype.buildGameMapForCategory = function (gamesMap, category) {
        if (category.games) {
            category.games.forEach(function (game) {
                if (!gamesMap.has(game.gameExternalId)) {
                    gamesMap.set(game.gameExternalId, game);
                }
            });
        }
    };
    GamesFetchCoreXService.prototype.mapToCoreGame = function (category, game) {
        var _a, _b, _c, _d, _e;
        return {
            images: game.images,
            background: game.background,
            title: game.title,
            platform: game.platform,
            tags: game.tags,
            promo: game.promo,
            providerId: game.gameExternalId,
            vendor: ((_a = game.vendor) !== null && _a !== void 0 ? _a : game.providerName),
            id: game.slug,
            slug: game.slug,
            gameId: game.gameExternalId,
            lobbies: category.lobby ? [category.lobby] : [],
            category: category.slug,
            categories: [category.slug],
            desc: game.description,
            metaTitle: game.metaTitle,
            metaDescription: game.metaDescription,
            igcTags: [],
            igcDevices: '',
            searchTerms: (_c = (_b = game.searchTerms) === null || _b === void 0 ? void 0 : _b[this.language]) !== null && _c !== void 0 ? _c : '',
            customVendor: '',
            gamesShownInSearch: 100,
            cats: {},
            dimensions: {
                width: ((_d = game.dimensions) === null || _d === void 0 ? void 0 : _d.width) || 800,
                height: ((_e = game.dimensions) === null || _e === void 0 ? void 0 : _e.height) || 600,
            },
            authRequired: game.authRequired,
            aggregatesByCurrency: game.aggregatesByCurrency,
        };
    };
    GamesFetchCoreXService.prototype.mapToCoreCategory = function (category) {
        var _a, _b, _c;
        return {
            name: category.name,
            id: category.slug,
            slug: category.slug,
            lobby: category.lobby || this.defaultLobby,
            main: category.main,
            title: category.title || category.name,
            metaDescription: category.metaDescription,
            metaTitle: category.metaTitle,
            description: category.description,
            images: category.images,
            hide: category.hide,
            displayPreference: category.displayPreference,
            key: category.key,
            games: new Set(((_a = category.games) !== null && _a !== void 0 ? _a : []).map(function (game) { return game.slug; })),
            rows: category.rows,
            position: (_b = category.position) !== null && _b !== void 0 ? _b : 1,
            subCategories: new Set(((_c = category.subs) !== null && _c !== void 0 ? _c : []).map(function (sub) { return sub.slug; })),
        };
    };
    GamesFetchCoreXService.prototype.subscribeToGameCorex = function () {
        var _this = this;
        window.queryBus.subscribe(GAME_COREX_FETCH_BY_ID, function (event) { return __awaiter(_this, void 0, void 0, function () {
            var gameId, db, game;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        gameId = event.payload;
                        if (!gameId) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, openGamesCorexDB()];
                    case 1:
                        db = _a.sent();
                        return [4 /*yield*/, db.getById(gameId)];
                    case 2:
                        game = _a.sent();
                        db.close();
                        return [2 /*return*/, game];
                }
            });
        }); });
    };
    return GamesFetchCoreXService;
}());

export { GamesFetchCoreXService };
